// components/Form2306Form.js
import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import formAPI from "../api/formAPI";
import ChartOfAccountsSearch from "./ChartOfAccountsSearch";
import CustomerSearch from "./CustomerSearch";
import MoneyInput from "./MoneyInput";
import Button from "./Button";
import notify from "../lib/notify";
import useAtcCodes from "../hooks/useAtcCodes";

const getFYKey = (clientId) => (clientId ? `activeFiscalYear:${clientId}` : null);

const resolveFiscalYear = (clientId, selectedYear) => {
  const fromSelected = parseInt(selectedYear, 10);
  if (fromSelected && !isNaN(fromSelected)) return fromSelected;

  const key = getFYKey(clientId);
  const headerFY = key ? parseInt(localStorage.getItem(key), 10) : null;
  return headerFY && !isNaN(headerFY) ? headerFY : null;
};

const newRow = () => ({
  description: "",
  atcCode: "",
  payeeVendor: null,
  payeeVendorId: null,
  payeeName: "",
  payeeTin: "",
  grossAmount: "",
  withheldRate: "1.00",
  withheldAmount: "",
  netAmount: "",
  chartOfAccount: null,
  chartOfAccountName: "",
  transactionDate: new Date().toISOString().split("T")[0],
});

const recalcAmounts = (row) => {
  const grossAmount = parseFloat(row.grossAmount) || 0;
  const withheldRate = parseFloat(row.withheldRate) || 0;
  const withheldAmount = (grossAmount * withheldRate) / 100;
  const netAmount = grossAmount - withheldAmount;

  return {
    ...row,
    withheldAmount: withheldAmount.toFixed(2),
    netAmount: netAmount.toFixed(2),
  };
};

const Form2306Form = ({ client, onRecordAdded, selectedQuarter, selectedYear }) => {
  const [records, setRecords] = useState([newRow()]);
  const [submitting, setSubmitting] = useState(false);

  // 2306 is final withholding, so the ATC list is the WF_PH set — a different
  // set from 2307's. Reference table, not a hardcoded list (see useAtcCodes).
  const { options: atcOptions, byCode: atcByCode } = useAtcCodes("2306");

  const fiscalYearNum = useMemo(() => resolveFiscalYear(client?._id, selectedYear), [client?._id, selectedYear]);

  const handleAddRow = () => {
    if (records.length < 5) setRecords((prev) => [...prev, newRow()]);
  };

  const handleRemoveRow = (index) => {
    if (records.length <= 1) return;
    const next = [...records];
    next.splice(index, 1);
    setRecords(next);
  };

  const handleRecordChange = (index, field, value) => {
    const next = [...records];
    const row = { ...next[index], [field]: value };

    // Recalculate only if grossAmount or withheldRate changed, but not if withheldAmount is manually edited
    if (field === "grossAmount" || field === "withheldRate") {
      next[index] = recalcAmounts(row);
    } else if (field === "withheldAmount") {
      // Manual override - recalculate netAmount only
      const grossAmount = parseFloat(row.grossAmount) || 0;
      const withheldAmount = parseFloat(value) || 0;
      const netAmount = grossAmount - withheldAmount;
      next[index] = { ...row, withheldAmount: value, netAmount: netAmount.toFixed(2) };
    } else {
      next[index] = row;
    }

    setRecords(next);
  };

  // Both fields must land in ONE update: handleRecordChange reads `records` from
  // the closure, so two sequential calls would both start from the same array
  // and the second would drop the first field.
  const handleAtcSelect = (index, atcCode) => {
    const known = atcByCode[atcCode];
    setRecords((prev) => {
      const next = [...prev];
      // Unknown code -> keep the existing rate; never guess one.
      const row = known
        ? { ...next[index], atcCode, withheldRate: String(known.ratePercent) }
        : { ...next[index], atcCode };
      next[index] = known ? recalcAmounts(row) : row;
      return next;
    });
  };

  const handleCustomerSelect = (index, customer) => {
    const next = [...records];
    let row = { ...next[index] };

    if (!customer) {
      row = { ...row, payeeVendor: null, payeeVendorId: null, payeeName: "", payeeTin: "" };
    } else {
      row = {
        ...row,
        payeeVendor: customer,
        payeeVendorId: customer._id,
        payeeName: customer.name || "",
        payeeTin: customer.tin || "",
      };
    }

    next[index] = recalcAmounts(row);
    setRecords(next);
  };

  const handleChartOfAccountSelect = (index, account) => {
    const next = [...records];

    if (!account) {
      next[index].chartOfAccount = null;
      next[index].chartOfAccountName = "";
    } else {
      next[index].chartOfAccount = account._id;
      next[index].chartOfAccountName = `${account.accountNumber} - ${account.accountName}`;
      if (!next[index].description) next[index].description = account.accountName;
    }

    setRecords(next);
  };

  const totals = useMemo(() => {
    return records.reduce(
      (acc, r) => {
        const gross = parseFloat(r.grossAmount) || 0;
        const withheld = parseFloat(r.withheldAmount) || 0;
        const net = parseFloat(r.netAmount) || 0;

        acc.gross += gross;
        acc.withheld += withheld;
        acc.net += net;
        if (gross > 0) acc.count += 1;
        return acc;
      },
      { gross: 0, withheld: 0, net: 0, count: 0 }
    );
  }, [records]);

  const handleSubmit = async () => {
    if (!client?._id) return notify.error("Couldn't save", "Select a client first.");
    if (!selectedQuarter) return notify.error("Couldn't save", "Select a quarter in VAT Management first.");
    if (!fiscalYearNum) return notify.error("Couldn't save", "Select a fiscal year from the header first.");

    const validRecords = records.filter(
      (r) => r.payeeVendorId && parseFloat(r.grossAmount) > 0
    );

    if (validRecords.length === 0) {
      return notify.error("Couldn't save", "Add at least one record with a customer and gross amount.");
    }

    const missingDescriptionIndex = validRecords.findIndex((r) => !r.description?.trim());
    if (missingDescriptionIndex !== -1) {
      return notify.error("Couldn't save", `Record ${missingDescriptionIndex + 1} needs a description.`);
    }

    try {
      setSubmitting(true);

      // ✅ map UI -> backend schema (baseAmount/taxWithheld/withholdingRate)
      const formattedRecords = validRecords.map((r) => ({
        description: r.description,
        vendor: r.payeeVendorId,

        // snapshots (optional fields in your model)
        payeeName: r.payeeName,
        payeeTin: r.payeeTin || "",

        baseAmount: parseFloat(r.grossAmount) || 0,
        withholdingRate: parseFloat(r.withheldRate) || 0,
        taxWithheld: parseFloat(r.withheldAmount) || 0,
        atcCode: r.atcCode || "",

        chartOfAccount: r.chartOfAccount,
        chartOfAccountName: r.chartOfAccountName,
        transactionDate: r.transactionDate || new Date().toISOString(),
      }));

      const response = await formAPI.createForm2306Records(client._id, formattedRecords);
      const savedRecords = response?.data || [];

      setRecords([newRow()]);
      // Pass the saved records up — the parent's list is filtered by the
      // currently viewed quarter, so it needs to know each record's actual
      // (transactionDate-derived) period to decide whether to switch the
      // view to show what was just saved instead of silently not showing it.
      onRecordAdded?.(savedRecords);

      const count = validRecords.length;
      // The backend derives each record's actual month/fiscalYear from its own
      // transactionDate — build the confirmation message from what was
      // actually saved. A batch can span more than one period if rows have
      // different transaction dates.
      const periodLabels = new Set(
        savedRecords
          .filter((r) => r?.fiscalYear && r?.month)
          .map((r) => format(new Date(r.fiscalYear, r.month - 1, 1), "MMMM yyyy"))
      );
      const periodText = periodLabels.size === 1
        ? ` for ${Array.from(periodLabels)[0]}`
        : periodLabels.size > 1
          ? ` across ${periodLabels.size} different periods`
          : "";
      notify.success("Saved", `${count} Form 2306 record${count !== 1 ? "s" : ""}${periodText}.`);
    } catch (error) {
      console.error("Error creating Form 2306 records:", error);
      notify.error("Couldn't save", error?.message || "Unknown error — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-ink">Form 2306 (EWT) records</h3>
        <p className="text-sm text-ink-body mt-1">Expanded withholding tax (EWT) — BIR Form 2306</p>
        <div className="mt-2 text-xs text-ink-body">
          Client: {client?.name || "—"} • Quarter {selectedQuarter || "—"} • FY {fiscalYearNum || "—"}
        </div>
      </div>

      <div>
        <div className="space-y-4">
          {records.map((record, index) => (
            <div key={index} className="bg-white p-4 rounded-lg border border-ink-line">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                {/* Vendor */}
                <div className="lg:col-span-4 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-ink-body mb-1">Payor (customer) *</label>
                    <CustomerSearch
                      value={record.payeeVendor}
                      onSelect={(customer) => handleCustomerSelect(index, customer)}
                      placeholder="Search customer by name, email, phone, or TIN..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-body mb-1">Payee TIN</label>
                    <input
                      type="text"
                      value={record.payeeTin}
                      onChange={(e) => handleRecordChange(index, "payeeTin", e.target.value)}
                      className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="XXX-XXX-XXX-XXX"
                      readOnly={!!record.payeeVendorId}
                    />
                  </div>
                </div>

                {/* Description + Date */}
                <div className="lg:col-span-4 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-ink-body mb-1">Description *</label>
                    <ChartOfAccountsSearch
                      value={record.chartOfAccountName || record.description}
                      onChange={(value) => handleRecordChange(index, "description", value)}
                      onSelect={(account) => handleChartOfAccountSelect(index, account)}
                      placeholder="Description or search chart of accounts"
                      showLinkedBadge={false}
                    />
                    {record.payeeVendorId && !record.description?.trim() && (
                      <p className="mt-1 text-xs text-danger-500">Description is required.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-body mb-1">Transaction date</label>
                    <input
                      type="date"
                      value={record.transactionDate}
                      onChange={(e) => handleRecordChange(index, "transactionDate", e.target.value)}
                      className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Amounts */}
                <div className="lg:col-span-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-ink-body mb-1">ATC Code</label>
                      <select
                        value={record.atcCode}
                        onChange={(e) => handleAtcSelect(index, e.target.value)}
                        className="w-full border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {atcOptions.map((o) => (
                          <option key={o.value} value={o.value} title={o.description}>{o.label}</option>
                        ))}
                      </select>
                      {atcByCode[record.atcCode]?.needsReview && (
                        <p className="mt-1 text-xs text-warning-600">
                          Unverified rate — confirm with the accountant before filing.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-ink-body mb-1">Rate (%)</label>
                      <input
                        type="number"
                        value={record.withheldRate}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                            handleRecordChange(index, "withheldRate", val);
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (isNaN(val) || val < 0) handleRecordChange(index, "withheldRate", "0");
                          else if (val > 100) handleRecordChange(index, "withheldRate", "100");
                        }}
                        className="w-full border border-ink-line rounded px-3 py-2 text-sm text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g. 3"
                        min="0"
                        max="100"
                        step="any"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-ink-body mb-1">Gross *</label>
                      <MoneyInput
                        value={record.grossAmount}
                        onValueChange={(value) =>
                          handleRecordChange(index, "grossAmount", value)
                        }
                        className="w-full text-right"
                        decimals={2}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-ink-body mb-1">Withheld</label>
                      <MoneyInput
                        value={record.withheldAmount}
                        onValueChange={(value) =>
                          handleRecordChange(index, "withheldAmount", value)
                        }
                        className="w-full text-right"
                        decimals={2}
                      />
                    </div>

                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-ink-body mb-1">Net</label>
                      <input
                        type="number"
                        value={record.netAmount}
                        readOnly
                        className="w-full border border-ink-line rounded px-3 py-2 text-sm text-right tabular-nums bg-ink-canvas text-ink-body"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {records.length > 1 && (
                <div className="mt-4 flex justify-end">
                  <button type="button" onClick={() => handleRemoveRow(index)} className="text-primary-500 font-medium text-sm">
                    Remove record
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/*records.length < 5 && (
          <div className="mt-4">
            <button type="button" onClick={handleAddRow} className="text-primary-500 font-medium text-sm">
              + Add another record
            </button>
          </div>
        )*/}

        <div className="mt-6 p-4 bg-ink-canvas rounded-lg">
          <div className="flex justify-between items-center">
            <div className="text-xs text-ink-body">
              {totals.count} valid record{totals.count !== 1 ? "s" : ""} • FY {fiscalYearNum || "—"}
            </div>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !selectedQuarter || !fiscalYearNum || totals.count === 0}
              loading={submitting}
              variant="primary"
              size="md"
            >
              {submitting ? "Creating…" : `Create ${totals.count} record${totals.count !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form2306Form;